import React, { useContext, useEffect } from 'react';
import * as yup from 'yup';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import { LocationContactInDTO, LocationContactOutDTO } from '@api/ringil-api.v2';
import { findErrorMessage } from '@utils/validationUtils.ts';
import useForm from '@hooks/useForm/useForm.tsx';
import { InputText } from 'primereact/inputtext';
import { Field } from '@components/framework/formfield/Field.tsx';
import { FieldLabel } from '@components/framework/formfield/FieldLabel.tsx';
import InputYesNo from '@components/ringil3/Input/InputSelect/InputYesNo.tsx';
import LocationSidebarHeader from '@components/RingilComponents/LocationSidebarHeader/LocationSidebarHeader.tsx';
import { isNotBlank } from '@utils/utils.tsx';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  onCompleted: () => void;
  locationId: string;
  editContact?: LocationContactOutDTO;
}

interface FormData {
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  automaticPrefillToNotify: boolean;
}

const initialData: FormData = {
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  automaticPrefillToNotify: false,
};

const AddEditContactPersonSidebar: React.FC<ComponentProps> = ({ visible, onHide, onCompleted, locationId, editContact }) => {
  const { tr } = useTranslationLgs();
  const { createLocationContact, updateLocationContact } = useContext(ApiContext);

  const validationSchema = yup.object().shape({
    contactEmail: yup.string().nullable().email(tr(`validation.valid_email`, 'Value has to be valid email address.')),
    // mirrors the BE rule: at least one of name/phone/email non-blank
    contactName: yup.string().test('atLeastOne', tr('LocalityDetail.contactAtLeastOne', 'Fill at least one of name, e-mail or phone'), function () {
      const v = this.parent as FormData;
      return isNotBlank(v.contactName) || isNotBlank(v.contactEmail) || isNotBlank(v.contactPhone);
    }),
  });

  const form = useForm<FormData>(validationSchema, initialData, values => save(values), false, false);
  const names = form.names;

  useEffect(() => {
    if (visible) {
      form.setValues(
        editContact
          ? {
              contactName: editContact.contactName,
              contactEmail: editContact.contactEmail,
              contactPhone: editContact.contactPhone,
              automaticPrefillToNotify: editContact.automaticPrefillToNotify,
            }
          : initialData,
      );
    }
  }, [visible, editContact]);

  const save = (values: FormData) => {
    const body: LocationContactInDTO = {
      contactName: isNotBlank(values.contactName) ? values.contactName : null,
      contactEmail: isNotBlank(values.contactEmail) ? values.contactEmail : null,
      contactPhone: isNotBlank(values.contactPhone) ? values.contactPhone : null,
      automaticPrefillToNotify: values.automaticPrefillToNotify,
    };
    if (editContact) {
      updateLocationContact(locationId, editContact.contactId, body, () => onCompleted());
    } else {
      createLocationContact(locationId, body, () => onCompleted());
    }
  };

  return (
    <RingilSidebar
      visible={visible}
      heading={tr('LocalityDetail.contactPerson', 'Contact person')}
      onBack={onHide}
      onContinue={() => form.validateAndSend()}
      dataTestIdContinue={'location-contact-save'}
      dataTestIdBack={'location-contact-back'}
    >
      <div className={'flex flex-col gap-6'}>
        <LocationSidebarHeader locationId={locationId} variant={'locality'} />

        <Field label={tr('LocalityDetail.contactName', 'Name')} errorMessage={findErrorMessage(form.validationErrors, names.contactName, tr)}>
          <InputText value={form.values.contactName} onChange={e => form.setFieldValue(names.contactName, e.target.value)} data-testid={'location-contact-name'} />
        </Field>
        <Field label={tr('LocalityDetail.contactEmail', 'E-mail')} errorMessage={findErrorMessage(form.validationErrors, names.contactEmail, tr)}>
          <InputText value={form.values.contactEmail} onChange={e => form.setFieldValue(names.contactEmail, e.target.value)} data-testid={'location-contact-email'} />
        </Field>
        <Field label={tr('LocalityDetail.contactPhone', 'Phone')} errorMessage={findErrorMessage(form.validationErrors, names.contactPhone, tr)}>
          <InputText value={form.values.contactPhone} onChange={e => form.setFieldValue(names.contactPhone, e.target.value)} data-testid={'location-contact-phone'} />
        </Field>
        <div className={'flex flex-col gap-1'}>
          <FieldLabel
            label={tr('LocalityDetail.automaticPrefillToNotify', 'Automatically select for notification')}
            helpText={tr('LocalityDetail.automaticPrefillToNotifyHelp', 'When on, this contact is pre-selected for notifications')}
          />
          <InputYesNo
            value={form.values.automaticPrefillToNotify}
            onChange={v => form.setFieldValue(names.automaticPrefillToNotify, v)}
            dataTestId={'location-contact-autoNotify'}
          />
        </div>
      </div>
    </RingilSidebar>
  );
};

export default AddEditContactPersonSidebar;
